import React, { useState } from "react";
import {
  Button,
  Icon,
  GroupTextField,
  GroupTextFieldRow,
  Input,
} from "@elaniintc/goes-design-system";
import {
  DropdownList,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";

interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  iconConfig?: { type: string; name: string };
  avatar?: React.ReactNode;
  iconPosition?: string;
  indicator?: React.ReactNode;
}

interface MergeEntryFieldProps {
  index: number;
  entry: {
    name: string;
    nodeId: string;
    type: string;
  };
  isRequired: boolean;
  canDelete: boolean;
  disabled?: boolean;
  error?: string;
  onNameChange: (index: number, name: string) => void;
  onNodeChange: (index: number, nodeId: string, oldNodeId: string) => void;
  onDelete: (index: number) => void;
  getNodeOptions: (index: number) => SelectOption[];
  getLabelForValue?: (value: string) => string;
}

export const MergeEntryField: React.FC<MergeEntryFieldProps> = ({
  index,
  entry,
  isRequired,
  canDelete,
  disabled = false,
  error,
  onNameChange,
  onNodeChange,
  onDelete,
  getNodeOptions,
  getLabelForValue,
}) => {
  console.log(
    "[ENTRY] MergeEntryField rendered for index:",
    index,
    "disabled:",
    disabled,
  );
  const [searchTerm, setSearchTerm] = useState("");

  console.log("[ENTRY] About to call getNodeOptions for index:", index);
  const allOptions = getNodeOptions(index);
  console.log("[ENTRY] Got", allOptions.length, "options from getNodeOptions");
  const options: IDropdownListItem[] = allOptions
    .filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((opt) => ({
      value: opt.value,
      label: opt.label,
      icon: opt.iconConfig || { type: "fas", name: "cube" },
    }));

  const resolveSelectedItem = () => {
    if (!entry.nodeId) return undefined;

    const inFiltered = options.find((opt) => opt.value === entry.nodeId);
    if (inFiltered) return inFiltered;

    const inAll = allOptions.find((opt) => opt.value === entry.nodeId);
    if (inAll) {
      return {
        value: inAll.value,
        label: inAll.label,
        icon: inAll.iconConfig || { type: "fas", name: "cube" },
      };
    }

    if (getLabelForValue) {
      return {
        value: entry.nodeId,
        label: getLabelForValue(entry.nodeId),
        icon: { type: "fas", name: "cube" },
      };
    }

    return {
      value: entry.nodeId,
      label: entry.nodeId,
      icon: { type: "fas", name: "cube" },
    };
  };

  const selectedValue = resolveSelectedItem();

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <GroupTextField label={`Entrada ${index + 1}`} required={isRequired}>
          <GroupTextFieldRow>
            <Input
              className="w-full"
              value={entry.name}
              onChange={(e) => onNameChange(index, e.target.value)}
              placeholder="Ej: Entrada Aprobada"
              disabled={disabled}
            />
          </GroupTextFieldRow>
          <GroupTextFieldRow>
            <DropdownList
              options={options}
              selectedValue={selectedValue}
              placeholder="Selecciona un nodo"
              widthClassName="w-full"
              group="down"
              disabled={disabled}
              search={{
                placeholder: "Buscar nodo...",
                query: searchTerm,
                onSearch: setSearchTerm,
              }}
              toggleSelection={(item) => {
                onNodeChange(index, item.value, entry.nodeId);
              }}
            />
          </GroupTextFieldRow>
        </GroupTextField>
        {error && (
          <div className="flex items-center gap-1.5 mt-1 text-danger">
            <Icon
              type="fas"
              name="exclamation-circle"
              className="w-3.5 h-3.5"
            />
            <span className="text-xs font-medium">{error}</span>
          </div>
        )}
      </div>

      {canDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0 mb-8"
          onClick={() => onDelete(index)}
          aria-label="Eliminar entrada"
        >
          <Icon type="far" name="xmark" className="text-body-quiet" />
        </Button>
      )}
    </div>
  );
};
